import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, Crosshair, Loader2, MapPin, Siren, Stethoscope, UserRound } from "lucide-react";
import { BookingApi, GeoApi, PaymentApi } from "../../api/services";
import { AddressSearch } from "../../components/AddressSearch";
import { errorMessage } from "../../api/client";
import { location, network } from "../../platform";
import { useAuth } from "../../auth/AuthProvider";
import { Call911 } from "../../components/common";
import { MapView, MapMarker } from "../../maps/MapView";
import { useRoute } from "../../maps/useRoute";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { peso } from "../../lib/format";

type Pt = { lat: number; lng: number; address: string };
const CONDITIONS = ["Difficulty breathing", "Chest pain", "Injury / accident", "Unconscious", "Stroke signs", "Pregnancy / labor", "Other"];
const ADDON_ICON: Record<string, any> = { doctor: Stethoscope, nurse: UserRound };

function ServiceCard({ id, active, onClick }: { id: "ptv" | "sos"; active: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} data-testid={`service-${id}`}
      className={`relative flex flex-1 flex-col items-start gap-1 rounded-2xl border-2 p-3 text-left ${active ? (id === "sos" ? "border-[#FFC400] bg-[#FFF4CC]" : "border-[#036248] bg-[#EEF7F3]") : "border-slate-200 bg-white"}`}>
      <img src="/ptv-van.png" alt="" className="h-12 w-auto object-contain" />
      {id === "sos" && <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-[#FFC400] px-2 py-0.5 text-[10px] font-bold text-[#003F32]"><Siren size={11} />SOS</span>}
      <span className="font-display text-sm font-bold text-[#003F32]">{id === "ptv" ? "PTV" : "SOS Ambulance"}</span>
      <span className="text-[11px] text-slate-600">{id === "ptv" ? "Non-emergency patient transport" : "Emergency ambulance pickup"}</span>
    </button>
  );
}

export default function Book({ initial }: { initial: "ptv" | "sos" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [service, setService] = useState<"ptv" | "sos">(initial);
  const [pickup, setPickup] = useState<Pt | null>(null);
  const [dest, setDest] = useState<Pt | null>(null);
  const [pinFor, setPinFor] = useState<"pickup" | "dest">("pickup");
  const [addons, setAddons] = useState<string[]>([]);
  const [freeRide, setFreeRide] = useState(false);
  const { data: mem } = useQuery({ queryKey: ["membership"], queryFn: PaymentApi.membership });
  const freeLeft: number = mem?.active?.free_rides_left || 0;
  const [condition, setCondition] = useState("");
  const [notes, setNotes] = useState("");
  const [gpsErr, setGpsErr] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "failed">("idle");
  const [err, setErr] = useState("");
  const [online, setOnline] = useState(network.isOnline());
  const requestId = useMemo(() => crypto.randomUUID(), []);
  useEffect(() => network.subscribe(setOnline), []);
  useEffect(() => setService(initial), [initial]);

  const { data: q, isFetching } = useQuery({
    queryKey: ["quote", service, pickup?.lat, pickup?.lng, dest?.lat, dest?.lng, addons.join()],
    queryFn: () => BookingApi.quote({ service, pickup, destination: dest, addons }),
    enabled: !!pickup && online,
  });
  const { data: mine } = useQuery({ queryKey: ["bookings"], queryFn: BookingApi.mine });
  const active = (mine || []).find((b) => !["completed", "cancelled", "scheduled"].includes(b.status) && (service === "sos" ? b.type === "emergency" : b.type === "ptv"));
  const { route, routed } = useRoute(pickup, dest);

  const [gpsBusy, setGpsBusy] = useState(false);
  const fillAddress = async (lat: number, lng: number, set: (fn: (p: Pt | null) => Pt | null) => void) => {
    try {
      const r = await GeoApi.reverse(lat, lng);
      if (r.address) set((p) => (p && p.lat === lat && p.lng === lng ? { ...p, address: r.address } : p));
    } catch { /* address stays editable */ }
  };
  const useGps = async () => {
    setGpsErr(""); setGpsBusy(true);
    try { const p = await location.getCurrentPosition(); setPickup({ lat: p.lat, lng: p.lng, address: "" }); setPinFor("dest"); await fillAddress(p.lat, p.lng, setPickup); }
    catch (e: any) { setGpsErr(e.message); }
    finally { setGpsBusy(false); }
  };
  const onPick = (lat: number, lng: number) => {
    if (pinFor === "pickup") { setPickup({ lat, lng, address: "" }); setPinFor("dest"); fillAddress(lat, lng, setPickup); }
    else { setDest({ lat, lng, address: "" }); fillAddress(lat, lng, setDest); }
  };
  const toggleAddon = (a: string) => setAddons((x) => (x.includes(a) ? x.filter((y) => y !== a) : [...x, a]));

  const markers: MapMarker[] = [
    ...(q?.available_units || []).map((u: any, i: number) => ({ id: `u${i}`, lat: u.lat, lng: u.lng, kind: "vehicle" as const, label: `${u.vehicle_type}${u.is_simulated ? " (simulated)" : ""} · ${u.km} km` })),
    ...(pickup ? [{ id: "p", lat: pickup.lat, lng: pickup.lng, kind: "pickup" as const, label: "Pickup" }] : []),
    ...(dest ? [{ id: "d", lat: dest.lat, lng: dest.lng, kind: "destination" as const, label: "Destination" }] : []),
  ];
  const needsDest = service === "ptv";
  const canSend = online && pickup && q?.in_service_area && (!needsDest || dest) && (service === "ptv" || condition) && state !== "sending";
  const units = q?.available_units || [];
  const sim = units.filter((u: any) => u.is_simulated).length;

  const send = async () => {
    setConfirm(false);
    if (!network.isOnline()) { setState("failed"); setErr("You are offline. The request was NOT sent."); return; }
    setState("sending"); setErr("");
    try {
      const b = await BookingApi.create({ type: service === "sos" ? "emergency" : "ptv", service, pickup, destination: dest, addons,
        condition: service === "sos" ? condition : "PTV transport", notes, contact_phone: user.phone, client_request_id: requestId, use_free_ride: freeRide && freeLeft > 0 });
      if (!b?.id) throw new Error("No confirmation from server");
      nav(`/patient/bookings/${b.id}?confirmed=1`, { replace: true });
    } catch (e) { setState("failed"); setErr(errorMessage(e)); }
  };

  if (active) {
    return (
      <div className="fp-card space-y-3" data-testid="book-already-active">
        <p className="font-semibold">You already have an active {service === "sos" ? "SOS" : "PTV"} booking ({active.code}).</p>
        <Link to={`/patient/bookings/${active.id}`} className="fp-btn-primary w-full" data-testid="book-open-active">Track my ride</Link>
      </div>
    );
  }

  return (
    <div className="-mx-4 -mt-5 lg:mx-0 lg:mt-0" data-testid="book-page">
      <div className="relative">
        <MapView className="h-[42vh] rounded-none border-0 lg:h-[50vh] lg:rounded-2xl" markers={markers} route={route} routeDashed={!routed} onPick={onPick} testId="book-map" />
        <div className="absolute left-3 right-3 top-3 z-[400] flex gap-2">
          <button onClick={() => setPinFor("pickup")} data-testid="pin-pickup-toggle" className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold shadow ${pinFor === "pickup" ? "bg-[#003F32] text-white" : "bg-white text-[#003F32]"}`}><MapPin size={12} className="inline" /> {pickup ? "Pickup set" : "Tap map: pickup"}</button>
          <button onClick={() => setPinFor("dest")} data-testid="pin-destination-toggle" className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold shadow ${pinFor === "dest" ? "bg-[#FFC400] text-[#003F32]" : "bg-white text-[#003F32]"}`}><MapPin size={12} className="inline" /> {dest ? "Destination set" : `Tap map: destination${needsDest ? "" : " (optional)"}`}</button>
        </div>
      </div>
      <div className="relative -mt-5 space-y-4 rounded-t-3xl bg-gradient-to-b from-white to-[#F1F9F4] px-4 pb-6 pt-5 lg:mt-4 lg:rounded-3xl">
        <div className="flex gap-3"><ServiceCard id="ptv" active={service === "ptv"} onClick={() => setService("ptv")} /><ServiceCard id="sos" active={service === "sos"} onClick={() => setService("sos")} /></div>
        {service === "sos" && <Call911 compact />}
        {!online && <div role="alert" className="rounded-2xl bg-slate-900 p-3 text-sm text-white" data-testid="emergency-offline-warning">You are offline. Bookings cannot be sent. In an emergency call <a href="tel:911" className="font-bold underline">911</a>.</div>}

        <div className="space-y-2 rounded-2xl bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#FFC400]" />
            <AddressSearch value={pickup} placeholder="Search pickup address or landmark" testId="book-pickup-address" loading={gpsBusy}
              onSelect={(p) => { setPickup(p); setPinFor("dest"); }} onType={(t) => pickup && setPickup({ ...pickup, address: t })} />
            <button onClick={useGps} disabled={gpsBusy} className="fp-btn-secondary h-11 px-3" aria-label="Use my location" data-testid="book-gps-button"><Crosshair size={16} /></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#036248]" />
            <AddressSearch value={dest} placeholder={`Search destination (hospital / clinic)${needsDest ? "" : " — optional"}`} testId="book-destination-address"
              onSelect={setDest} onType={(t) => dest && setDest({ ...dest, address: t })} />
          </div>
          {gpsErr && <p className="text-xs text-amber-800" data-testid="book-gps-error">{gpsErr}</p>}
        </div>

        {service === "sos" && (
          <div className="flex flex-wrap gap-2" data-testid="emergency-condition-options">
            {CONDITIONS.map((c) => <button key={c} type="button" onClick={() => setCondition(c)} data-testid={`condition-${c.split(" ")[0].toLowerCase()}`}
              className={`min-h-[40px] rounded-xl border px-3 text-xs font-medium ${condition === c ? "border-[#036248] bg-[#036248] text-white" : "border-slate-300 bg-white"}`}>{c}</button>)}
          </div>
        )}

        <div>
          <p className="fp-label mb-2">Medical add-ons</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(q?.addons_catalog || { doctor: { name: "Bring a Doctor" }, nurse: { name: "Bring a Nurse" } }).map(([id, a]: any) => {
              const Icon = ADDON_ICON[id];
              const on = addons.includes(id);
              return (
                <button key={id} type="button" onClick={() => toggleAddon(id)} data-testid={`addon-${id}`}
                  className={`flex items-center gap-2 rounded-2xl border-2 p-3 text-left ${on ? "border-[#FFC400] bg-[#FFF4CC]" : "border-slate-200 bg-white"}`}>
                  <Icon size={20} className="text-[#036248]" />
                  <span><span className="block text-sm font-semibold text-[#003F32]">{a.name}</span>{a.fee != null && <span className="text-xs text-slate-600">+{peso(a.fee)}</span>}</span>
                </button>
              );
            })}
          </div>
        </div>

        <textarea className="fp-input h-16 py-2" placeholder="Notes for the driver (wheelchair, floor, landmark)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-notes-input" />

        {pickup && q && (
          <div className="space-y-2 rounded-2xl bg-white p-4 shadow-sm" data-testid="book-quote">
            {!q.in_service_area ? (
              <p className="text-sm font-semibold text-amber-900" data-testid="service-unavailable-notice">FASTPASS is not available at this pickup location (Metro Cebu only). In an emergency call 911.</p>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span data-testid="book-available-drivers">{units.length ? `${units.length} driver${units.length > 1 ? "s" : ""} available${sim ? ` (${sim} simulated test unit${sim > 1 ? "s" : ""})` : ""}` : "No drivers available right now"}</span>
                  {q.pickup_eta_minutes != null && <span className="font-semibold text-[#036248]" data-testid="book-pickup-eta">Pickup ~{q.pickup_eta_minutes} min</span>}
                </div>
                {q.trip && <p className="text-xs text-slate-600" data-testid="book-trip-info">Trip {q.trip.km} km · ~{q.trip.minutes} min {q.trip.routed ? "by road" : "(approximate)"}</p>}
                <div className="space-y-1 border-t border-slate-100 pt-2 text-sm">
                  <div className="flex justify-between"><span>{q.quote.service_name} base</span><span>{peso(q.quote.base)}</span></div>
                  {q.quote.km != null && <div className="flex justify-between"><span>Distance ({q.quote.km} km × {peso(q.quote.per_km)})</span><span>{peso(q.quote.distance_fee)}</span></div>}
                  {q.quote.addons.map((a: any) => <div key={a.id} className="flex justify-between"><span>{a.name}</span><span>{peso(a.fee)}</span></div>)}
                  <div className="flex justify-between border-t border-slate-100 pt-1 text-base font-bold text-[#003F32]"><span>Estimated total</span><span data-testid="book-total">{freeRide && freeLeft > 0 ? <><s className="mr-2 text-sm font-normal text-slate-400">{peso(q.quote.total)}</s>{peso(0)}</> : peso(q.quote.total)}</span></div>
                </div>
                {freeLeft > 0 && (
                  <label className="flex items-center gap-2 rounded-xl bg-[#FFF4CC] p-3 text-sm font-semibold text-[#003F32]" data-testid="book-free-ride-toggle">
                    <input type="checkbox" checked={freeRide} onChange={(e) => setFreeRide(e.target.checked)} data-testid="book-free-ride-checkbox" />
                    Use 1 free ride ({freeLeft} left this year)
                  </label>
                )}
              </>
            )}
          </div>
        )}
        {isFetching && <p className="text-xs text-slate-500"><Loader2 size={12} className="inline animate-spin" /> Updating fare…</p>}

        {state === "failed" && (
          <div role="alert" className="flex gap-3 rounded-2xl border-2 border-[#FFC400] bg-[#FFF4CC] p-4 text-[#003F32]" data-testid="emergency-request-failed">
            <AlertOctagon className="shrink-0" /><div><p className="font-bold">Request NOT sent.</p><p className="text-sm">{err}</p></div>
          </div>
        )}
        <button className={`fp-btn h-14 w-full rounded-2xl text-base ${service === "sos" ? "bg-[#FFC400] text-[#003F32] hover:bg-[#F4B400]" : "bg-[#FFC400] text-[#003F32] hover:bg-[#F4DF8C]"}`}
          disabled={!canSend} onClick={() => setConfirm(true)} data-testid="emergency-submit-button">
          {state === "sending" ? <><Loader2 className="animate-spin" /> Waiting for server confirmation…</> : service === "sos" ? <><Siren /> Request SOS Ambulance</> : "Book PTV"}
        </button>
        {!pickup && <p className="text-center text-xs text-slate-500">Set your pickup to see drivers and fare.</p>}
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="emergency-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{service === "sos" ? "Send SOS ambulance request?" : "Confirm PTV booking?"}</DialogTitle>
            <DialogDescription>{q ? `Estimated ${peso(freeRide && freeLeft > 0 ? 0 : q.quote.total)}${freeRide && freeLeft > 0 ? " (free ride)" : ""}. ` : ""}We will match the nearest suitable vehicle. You'll see confirmation only after our server accepts the request.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button className="fp-btn-secondary" onClick={() => setConfirm(false)} data-testid="emergency-confirm-cancel">Go back</button>
            <button className={service === "sos" ? "fp-btn-danger" : "fp-btn-primary"} onClick={send} data-testid="emergency-confirm-send">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
